import { createDarkTheme, createLightTheme } from '@fluentui/react-components';
import type { BrandVariants, Theme } from '@fluentui/react-components';

// Define custom tokens interface for machine health colors
export type CustomTokens = {
    // Health status - Primary variants (original colors)
    colorHealthOptimal1Bg: string;
    colorHealthOptimal1Fg: string;
    colorHealthOptimal1Stroke: string;
    colorHealthAcceptable1Bg: string;
    colorHealthAcceptable1Fg: string;
    colorHealthAcceptable1Stroke: string;
    colorHealthAttention1Bg: string;
    colorHealthAttention1Fg: string;
    colorHealthAttention1Stroke: string;
    colorHealthDanger1Bg: string;
    colorHealthDanger1Fg: string;
    colorHealthDanger1Stroke: string;
    colorHealthUnknown1Bg: string;
    colorHealthUnknown1Fg: string;
    colorHealthUnknown1Stroke: string;

    // Health status - Secondary variants (light colors)
    colorHealthOptimal2Bg: string;
    colorHealthOptimal2Fg: string;
    colorHealthOptimal2Stroke: string;
    colorHealthAcceptable2Bg: string;
    colorHealthAcceptable2Fg: string;
    colorHealthAcceptable2Stroke: string;
    colorHealthAttention2Bg: string;
    colorHealthAttention2Fg: string;
    colorHealthAttention2Stroke: string;
    colorHealthDanger2Bg: string;
    colorHealthDanger2Fg: string;
    colorHealthDanger2Stroke: string;
    colorHealthUnknown2Bg: string;
    colorHealthUnknown2Fg: string;
    colorHealthUnknown2Stroke: string;

    // Informative / in-progress blue, reserved for information and states still in progress —
    // deliberately NOT a health color. Same 1/2 convention as the health family: `1Bg` is the
    // vivid step (icon, dot, border), `2Bg` the tint and `2Fg` the label tone that clears AA.
    colorInfo1Bg: string;
    colorInfo1Stroke: string;
    colorInfo2Bg: string;
    colorInfo2Fg: string;

    // Event accents — used by RecordCard variants per event type.
    // Fg is the accent border / strong foreground; Bg is the soft tint
    // for plates and stat banners; Stroke is the border on tinted surfaces.
    // Only win and alarm need custom hues; the other event types use Fluent
    // neutrals directly (see useRecordAccent in cardEvents/components/accents.ts).
    colorEventWinFg: string;
    colorEventWinBg: string;
    colorEventWinStroke: string;
    colorEventAlarmFg: string;
    colorEventAlarmBg: string;
    colorEventAlarmStroke: string;
};

// Extend Theme type with custom tokens
export type CustomTheme = Theme & CustomTokens;

// Maper brand ramp — the single source of truth for the brand color.
// Consumers outside Fluent read index 80 (= colorBrandBackground in the light theme):
// see `primaryColor` in src/main/utilities/utils_functions.js and $primary in
// src/assets/themes/default/_variables.scss.
export const maperBrandRamp: BrandVariants = {
    10: '#010403',
    20: '#0E1C17',
    30: '#112F25',
    40: '#113D2E',
    50: '#0F4B39',
    60: '#0B5A43',
    70: '#01694E',
    80: '#24765C',
    90: '#3E836B',
    100: '#54907A',
    110: '#699D89',
    120: '#7EAB99',
    130: '#92B8A9',
    140: '#A7C6B9',
    150: '#BCD3C9',
    160: '#D1E1DA',
};

// Function to create light theme with custom tokens
export const createMaperLightTheme = (customTokens: CustomTokens): CustomTheme => {
    const theme = {
        ...createLightTheme(maperBrandRamp),
        ...customTokens,
    };

    // Tertiary (ghost) button grays: transparent at rest, gray fill on hover/pressed.
    // Fluent ships #F5F5F5 / #E0E0E0; the design spec asks for a slightly darker hover and a
    // lighter pressed. Set on the theme rather than in CustomButton.css so every `subtle`
    // surface stays consistent and the dark theme keeps its own ramp.
    theme.colorSubtleBackgroundHover = '#f0f0f0';
    theme.colorSubtleBackgroundPressed = '#e5e5e5';

    // Darker disabled states for better readability
    theme.colorNeutralForegroundDisabled = '#8a8a8a';
    theme.colorNeutralBackgroundDisabled = '#e8e8e8';
    theme.colorNeutralStrokeDisabled = '#c4c4c4';

    return theme;
};

// Function to create dark theme with custom tokens
export const createMaperDarkTheme = (customTokens: CustomTokens): CustomTheme => {
    const theme = {
        ...createDarkTheme(maperBrandRamp),
        ...customTokens,
    };

    // Override existing theme tokens
    theme.colorBrandForeground1 = maperBrandRamp[110];
    theme.colorBrandForeground2 = maperBrandRamp[120];

    // Darker disabled states for better readability
    theme.colorNeutralForegroundDisabled = '#8a8a8a';
    theme.colorNeutralBackgroundDisabled = '#383838';
    theme.colorNeutralStrokeDisabled = '#525252';

    return theme;
};
